import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Event } from "@/types/Event";
import { getEvent } from "@/lib/api/handlers/EventHandling";
import {
  buildEventVariables,
  withEventVariables,
} from "@/lib/utils/event-variables";

/** Projection covering every field `buildEventVariables` reads. Exported so a
 *  surface that already fetches its event (the builder pages) can widen its own
 *  projection instead of issuing a second request for the same document. */
export const EVENT_VAR_FIELDS =
  "name,start,end,timeZone,address,address2,zipCode,city,country,locationName,companyName,globalGuestLimit";

export function eventVariablesKey(eventId: string | null | undefined) {
  return ["eventVariables", eventId, EVENT_VAR_FIELDS];
}

/**
 * Loads the event and returns an `enrich(guest)` that merges the event-level
 * `{{...}}` variables ({{eventDate}}, {{eventTime}}, {{eventLocation}}, …) onto
 * a guest, formatted for that guest's language.
 *
 * <p>Every client-side preview resolves `{{...}}` purely against the guest
 * object, but event tokens aren't guest fields — so without this they render
 * blank in preview even though a real send fills them. Shared by the builder
 * preview pool (`usePreviewGuests`) and the send-email surfaces.
 *
 * <p>react-query rather than a hand-rolled effect, for three things the
 * hand-rolled version got wrong: it latched on a ref that was never cleared on
 * SUCCESS, so a change of `eventId` served the previous event's variables
 * forever; it swallowed failures and never retried; and it fetched once per
 * consumer, so a send dialog with three attachments and two companions issued
 * nine identical requests for one document.
 */
export function useEventVariables(
  eventId: string | undefined,
  active: boolean = true,
) {
  const query = useQuery({
    queryKey: eventVariablesKey(eventId),
    queryFn: async () => {
      const res = await getEvent(eventId as string, EVENT_VAR_FIELDS);
      // The handler answers with a ServerError object instead of throwing;
      // turn it into a rejection so react-query can retry and `ready` — the
      // flag that lets consumers delete rows — stays false meanwhile.
      if (!res || typeof res !== "object" || "isServerError" in res) {
        throw new Error("event unavailable");
      }
      return res as Event;
    },
    enabled: active && !!eventId,
    staleTime: 5 * 60_000,
  });

  const event = query.data ?? null;

  const enrich = useCallback(
    <T extends { language?: string | null }>(guest: T): T =>
      event
        ? withEventVariables(
            guest,
            buildEventVariables(event, { language: guest.language, eventId }),
          )
        : guest,
    [event, eventId],
  );

  // `ready` is the difference between "this event has no location" and "we
  // never got the event". Only the first justifies acting on an empty value —
  // a consumer that DELETES rows on one must stand down until then.
  //
  // `event` itself is handed out for the same reason one layer up: a preview
  // that renders the pass face needs the REAL event, or it falls back to a
  // stand-in and draws another customer's venue on the last screen before Send.
  return { enrich, ready: event !== null, event };
}
